<template>
    <div class="container-fluid report-page">
        <div class="page-heading d-sm-flex align-items-end justify-content-between mb-4">
            <div>
                <p class="page-eyebrow">REPORT</p>
                <h1 class="h3 mb-1 text-gray-800">Speaker &amp; session evaluation</h1>
                <p class="mb-0 text-muted">Review delegate feedback for every speaker in an event.</p>
            </div>
            <button class="btn btn-outline-primary mt-3 mt-sm-0" type="button" :disabled="!report || exporting" @click="exportCsv">
                <i class="pi pi-download mr-2" aria-hidden="true"></i>{{ exporting ? 'Exporting…' : 'Export CSV' }}
            </button>
        </div>

        <section class="report-filter card mb-4" aria-label="Report filter">
            <div class="card-body">
                <label for="report-event" class="mb-2 font-weight-bold">Event</label>
                <select id="report-event" v-model="selectedEventId" class="form-control" :disabled="eventsLoading" @change="loadReport">
                    <option value="">Select an event</option>
                    <option v-for="event in events" :key="event.id" :value="event.id">{{ event.name }} ({{ formatDate(event.startDate) }})</option>
                </select>
            </div>
        </section>

        <div v-if="loading" class="report-state"><i class="pi pi-spin pi-spinner mr-2" aria-hidden="true"></i>Loading evaluation report…</div>
        <div v-else-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
        <div v-else-if="!selectedEventId" class="report-state report-state--empty"><i class="pi pi-chart-bar" aria-hidden="true"></i><p>Select an event to view its speaker evaluation.</p></div>

        <template v-else-if="report">
            <section class="report-event-summary mb-4">
                <p class="section-eyebrow">EVENT EVALUATION</p>
                <h2>{{ report.eventName }}</h2>
                <p>{{ formatDate(report.eventStartDate) }} – {{ formatDate(report.eventEndDate) }}</p>
            </section>

            <div class="row report-metrics mb-4">
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Speakers" :value="report.totalSpeakerCount" icon="pi-users" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Speakers rated" :value="report.ratedSpeakerCount" icon="pi-check-circle" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Responses" :value="report.totalRatingCount" icon="pi-comment" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Average rating" :value="report.totalRatingCount ? formatRating(report.averageRating) + ' / 5' : '—'" icon="pi-star" /></div>
            </div>

            <section class="card report-table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table mb-0 report-table">
                            <thead>
                            <tr>
                                <th scope="col">Speaker</th>
                                <th scope="col">Session</th>
                                <th scope="col" class="text-center">Responses</th>
                                <th scope="col" class="text-center">Average rating</th>
                                <th scope="col" class="text-right">Feedback</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="report.speakers.length === 0">
                                <td colspan="5"><div class="table-empty">No speaker sessions were found for this event.</div></td>
                            </tr>
                            <tr v-for="speaker in report.speakers" :key="speaker.speakerId">
                                <td class="font-weight-bold">{{ speaker.speakerName }}</td>
                                <td><span v-for="session in speaker.sessionNames" :key="session" class="session-tag">{{ session }}</span></td>
                                <td class="text-center">{{ speaker.ratingCount }}</td>
                                <td class="text-center"><span class="rating-value" :class="{ 'rating-value--empty': !speaker.ratingCount }">{{ speaker.ratingCount ? formatRating(speaker.averageRating) + ' / 5' : '—' }}</span></td>
                                <td class="text-right"><button class="btn btn-sm btn-outline-primary" type="button" :disabled="speaker.reviews.length === 0" @click="showReviews(speaker)"><i class="pi pi-comments mr-1" aria-hidden="true"></i>{{ speaker.reviews.length }}</button></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </template>

        <Dialog v-model:visible="reviewsVisible" modal :header="selectedSpeaker ? `Feedback for ${selectedSpeaker.speakerName}` : 'Feedback'" :style="{ width: 'min(720px, 92vw)' }">
            <div v-if="selectedSpeaker" class="review-list">
                <article v-for="(review, index) in selectedSpeaker.reviews" :key="`${review.memberName}-${review.createdAt}-${index}`" class="review-item">
                    <div class="d-flex justify-content-between align-items-center mb-2"><strong>{{ review.memberName }}</strong><span class="rating-value">{{ review.rating }} / 5</span></div>
                    <p class="mb-1">{{ review.review }}</p>
                    <small class="text-muted">{{ formatDateTime(review.createdAt) }}</small>
                </article>
            </div>
        </Dialog>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import Dialog from 'primevue/dialog'
import moment from 'moment'
import apiService from '@/services/ApiService'

const MetricCard = {
    props: { label: String, value: [String, Number], icon: String },
    template: `<div class="card metric-card h-100"><div class="card-body"><div><p>{{ label }}</p><strong>{{ value }}</strong></div><i :class="['pi', icon]" aria-hidden="true"></i></div></div>`
}

export default defineComponent({
    name: 'Report',
    components: { Dialog, MetricCard },
    data() {
        return {
            events: [],
            eventsLoading: false,
            selectedEventId: '',
            report: null,
            loading: false,
            exporting: false,
            error: '',
            reviewsVisible: false,
            selectedSpeaker: null
        }
    },
    created() {
        this.loadEvents()
    },
    methods: {
        async loadEvents() {
            this.eventsLoading = true
            try {
                const response = await apiService.get('/event', { params: { page: 1, limit: 100 } })
                this.events = response.data.data || []
            } catch (error) {
                this.error = error.response?.data?.Message || 'Unable to load events.'
            } finally {
                this.eventsLoading = false
            }
        },
        async loadReport() {
            this.report = null
            this.error = ''
            this.selectedSpeaker = null
            if (!this.selectedEventId) return

            this.loading = true
            try {
                const response = await apiService.get('/report/speaker-evaluation', { params: { eventId: this.selectedEventId } })
                this.report = response.data
            } catch (error) {
                this.error = error.response?.data?.Message || 'Unable to load the speaker evaluation report.'
            } finally {
                this.loading = false
            }
        },
        showReviews(speaker) {
            this.selectedSpeaker = speaker
            this.reviewsVisible = true
        },
        async exportCsv() {
            this.exporting = true
            try {
                const response = await apiService.get('/report/speaker-evaluation/export', {
                    params: { eventId: this.selectedEventId },
                    responseType: 'blob'
                })
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }))
                const link = document.createElement('a')
                link.href = url
                link.download = 'speaker-evaluation.csv'
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            } catch (error) {
                this.$toast.add({ severity: 'error', summary: 'Export failed', detail: 'Unable to export this report.', life: 5000 })
            } finally {
                this.exporting = false
            }
        },
        formatRating(value) { return Number(value).toFixed(1) },
        formatDate(value) { return value ? moment(value).format('DD MMM YYYY') : '—' },
        formatDateTime(value) { return value ? moment(value).format('DD MMM YYYY, HH:mm') : '—' }
    }
})
</script>

<style scoped>
.page-heading{border-bottom:1px solid #e2e6ea;padding-bottom:1rem}.page-eyebrow,.section-eyebrow{color:#426cc6;font-size:.72rem;font-weight:700;letter-spacing:.08em;margin:0 0 .35rem}.report-filter{border:1px solid #e2e6ec;border-radius:.65rem}.report-filter .form-control{max-width:480px}.report-state{align-items:center;background:#fff;border:1px solid #e2e6ec;border-radius:.65rem;color:#5c6470;display:flex;justify-content:center;min-height:180px}.report-state--empty{flex-direction:column;gap:.75rem}.report-state--empty i{color:#426cc6;font-size:2rem}.report-state--empty p{margin:0}.report-event-summary{background:linear-gradient(120deg,#3656bf,#426cc6);border-radius:.65rem;color:#fff;padding:1.5rem}.report-event-summary .section-eyebrow{color:#dce7ff}.report-event-summary h2{font-size:1.35rem;margin-bottom:.4rem}.report-event-summary p:last-child{margin:0;opacity:.9}.metric-card{border:1px solid #e2e6ec;border-radius:.65rem}.metric-card .card-body{align-items:center;display:flex;justify-content:space-between;padding:1.15rem}.metric-card p{color:#6c757d;font-size:.75rem;font-weight:700;letter-spacing:.06em;margin:0 0 .35rem;text-transform:uppercase}.metric-card strong{color:#2b2f36;font-size:1.45rem}.metric-card .pi{color:#426cc6;font-size:1.75rem}.report-table-card{border:1px solid #e2e6ec;border-radius:.65rem;overflow:hidden}.report-table th{background:#f7f8fa;border-top:0;color:#59616e;font-size:.72rem;letter-spacing:.05em;text-transform:uppercase}.report-table td,.report-table th{padding:1rem;vertical-align:middle}.session-tag{background:#eef3ff;border-radius:999px;color:#3656bf;display:inline-block;font-size:.78rem;margin:.1rem .25rem .1rem 0;padding:.25rem .55rem}.rating-value{color:#a66c00;font-weight:700}.rating-value--empty{color:#8b939d}.table-empty{color:#6c757d;padding:2rem;text-align:center}.review-list{display:grid;gap:1rem}.review-item{border-bottom:1px solid #e9ecef;padding-bottom:1rem}.review-item:last-child{border-bottom:0;padding-bottom:0}.review-item p{white-space:pre-wrap}@media(max-width:576px){.report-table td,.report-table th{padding:.75rem}.report-event-summary{padding:1.25rem}}
</style>
